import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MatterDetailed } from '../../../../types/matters';
import { taskApi } from '../../../tasks/api/taskApi';
import { TaskDetailModal } from '../../../tasks/components/TaskDetailModal';
import { useAttorneys } from '../../hooks/useAttorneys';
import { useMatterBilling } from '../../hooks/useMatterBilling';
import TimeTracker from '../TimeTracker';
import UtbmsCodeSelector from '../../../billing/components/UtbmsCodeSelector';
import CourtMandatedBadge from '../../../tasks/components/CourtMandatedBadge';
import FormRequiredChip from '../../../tasks/components/FormRequiredChip';
import DeadlineTracePanel from '../../../tasks/components/DeadlineTracePanel';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../../../components/ui/dropdown-menu';
import type { Task, TaskStatus, TaskPriority, CreateTaskDto } from '../../../tasks/types/task.types';

interface TasksTabProps {
  matter: MatterDetailed;
}

const STATUS_OPTIONS: { value: TaskStatus; label: string; dot: string }[] = [
  { value: 'todo', label: 'To Do', dot: 'bg-yellow-400' },
  { value: 'in_progress', label: 'In Progress', dot: 'bg-blue-500' },
  { value: 'review', label: 'In Review', dot: 'bg-purple-500' },
  { value: 'done', label: 'Done', dot: 'bg-green-500' },
  { value: 'archived', label: 'Archived', dot: 'bg-gray-400' },
];

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/** Detect if a billing code follows UTBMS pattern (e.g. L110, A101, E107) */
const isUtbmsCode = (code: string | undefined | null): boolean =>
  !!code && /^[LAPBE]\d{3,}$/i.test(code.trim());

const getStatusColor = (status: string) => {
  switch (status) {
    case 'done': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    case 'in_progress': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
    case 'review': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
    case 'todo': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
    case 'archived': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
  }
};

const getStatusDot = (status: string) =>
  STATUS_OPTIONS.find(s => s.value === status)?.dot || 'bg-gray-400';

const getStatusLabel = (status: string) =>
  STATUS_OPTIONS.find(s => s.value === status)?.label || status;

const getPriorityBorder = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'border-l-red-500';
    case 'high': return 'border-l-amber-500';
    case 'medium': return 'border-l-blue-400';
    case 'low': return 'border-l-gray-300 dark:border-l-gray-600';
    default: return 'border-l-gray-300 dark:border-l-gray-600';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
    case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
    case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
  }
};

const getHoursProgressColor = (actual: number, estimated: number) => {
  const ratio = actual / estimated;
  if (ratio <= 0.75) return 'bg-green-500';
  if (ratio <= 1.0) return 'bg-amber-500';
  return 'bg-red-500';
};

const formatDate = (dateString?: string) => {
  if (!dateString) return 'No due date';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const isOverdue = (dueDate?: string, status?: string) => {
  if (!dueDate || status === 'done' || status === 'archived') return false;
  return new Date(dueDate) < new Date();
};

const getInitials = (task: Task): string => {
  const a = task.assignee;
  if (!a) return '';
  if (a.full_name) {
    const parts = a.full_name.trim().split(/\s+/);
    return (parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '');
  }
  return ((a.first_name?.[0] || '') + (a.last_name?.[0] || '')).toUpperCase();
};

const getAssigneeName = (task: Task): string => {
  const a = task.assignee;
  if (!a) return '';
  if (a.full_name) return a.full_name;
  if (a.first_name) return `${a.first_name} ${a.last_name || ''}`.trim();
  return a.email || '';
};

const TasksTab: React.FC<TasksTabProps> = ({ matter }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [deleteConfirmTaskId, setDeleteConfirmTaskId] = useState<string | null>(null);

  // Track which task owns the running timer.
  const [activeTimerTaskId, setActiveTimerTaskId] = useState<string | null>(null);

  // Phase grouping state
  const [groupByPhase, setGroupByPhase] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState('all');

  // Log time state
  const [loggingTimeForTaskId, setLoggingTimeForTaskId] = useState<string | null>(null);
  const [timeForm, setTimeForm] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    duration: '',
    rate: '',
    description: '',
  });
  const [timeSubmitting, setTimeSubmitting] = useState(false);

  // New task form state
  const [newTask, setNewTask] = useState<Partial<CreateTaskDto>>({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    assignee_id: '',
    estimated_hours: undefined,
    billing_code: '',
  });
  const { attorneys: realmUsers } = useAttorneys();
  const {
    createTimeEntry,
    createAndStartTimer,
    activeTimer,
    startTimer,
    pauseTimer,
    stopTimer,
    refresh: refreshBilling,
  } = useMatterBilling(matter.id);

  // If the active timer disappears externally (e.g. stopped from Billing tab), clear our tracking
  useEffect(() => {
    if (!activeTimer && activeTimerTaskId) {
      setActiveTimerTaskId(null);
    }
  }, [activeTimer, activeTimerTaskId]);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await taskApi.getByEngagement(matter.id);
      const data = result as any;
      setTasks(data.tasks || []);
    } catch {
      setError('Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }, [matter.id]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleCreateTask = async () => {
    if (!newTask.title?.trim()) return;
    try {
      setSubmitting(true);
      await taskApi.create({
        ...newTask as CreateTaskDto,
        engagementId: matter.id,
        clientId: matter.client_id,
      });
      setShowAddForm(false);
      setNewTask({ title: '', description: '', priority: 'medium', due_date: '', assignee_id: '', estimated_hours: undefined, billing_code: '' });
      await fetchTasks();
    } catch {
      setError('Failed to create task.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (taskId: string, status: TaskStatus) => {
    try {
      await taskApi.updateStatus(taskId, status);
      await fetchTasks();
    } catch {
      setError('Failed to update task status.');
    }
  };

  const handleDeleteClick = (taskId: string) => {
    setDeleteConfirmTaskId(taskId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmTaskId) return;
    try {
      await taskApi.delete(deleteConfirmTaskId);
      setDeleteConfirmTaskId(null);
      await fetchTasks();
    } catch {
      setError('Failed to delete task.');
      setDeleteConfirmTaskId(null);
    }
  };

  // Log time for a task
  const handleLogTime = (task: Task) => {
    setLoggingTimeForTaskId(task.id);
    setTimeForm({
      entry_date: new Date().toISOString().split('T')[0],
      duration: '',
      rate: '',
      description: task.title,
    });
  };

  const handleTimeSubmit = async () => {
    if (!loggingTimeForTaskId || !timeForm.duration) return;
    const task = tasks.find(t => t.id === loggingTimeForTaskId);
    if (!task) return;

    try {
      setTimeSubmitting(true);
      const duration = parseFloat(timeForm.duration);
      if (isNaN(duration) || duration <= 0) {
        setError('Duration must be a positive number.');
        return;
      }

      // Create time entry via billing hook — route UTBMS codes to utbms_code field
      const billingCode = task.billing_code || undefined;
      await createTimeEntry({
        entry_date: timeForm.entry_date,
        duration,
        rate: timeForm.rate ? parseFloat(timeForm.rate) : undefined,
        description: timeForm.description || task.title,
        activity_category: billingCode && !isUtbmsCode(billingCode) ? billingCode : undefined,
        utbms_code: billingCode && isUtbmsCode(billingCode) ? billingCode : undefined,
        entry_type: 'hourly',
        is_non_billable: false,
      });

      // Increment actual_hours on the task
      const currentHours = task.actual_hours || 0;
      await taskApi.update(task.id, { actual_hours: currentHours + duration });

      setLoggingTimeForTaskId(null);
      setTimeForm({ entry_date: '', duration: '', rate: '', description: '' });
      await Promise.all([fetchTasks(), refreshBilling()]);
    } catch {
      setError('Failed to log time.');
    } finally {
      setTimeSubmitting(false);
    }
  };

  const filteredTasks = tasks.filter(task => {
    const statusMatch = selectedStatus === 'all' || task.status === selectedStatus;
    const priorityMatch = selectedPriority === 'all' || task.priority === selectedPriority;
    return statusMatch && priorityMatch;
  });

  const taskSummary = {
    total: tasks.length,
    todo: tasks.filter(t => t.status === 'todo').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    done: tasks.filter(t => t.status === 'done').length,
    overdue: tasks.filter(t => isOverdue(t.due_date, t.status)).length,
  };

  // Hours budget KPI
  const hoursBudget = useMemo(() => {
    let actual = 0;
    let estimated = 0;
    for (const t of tasks) {
      actual += t.actual_hours || 0;
      estimated += t.estimated_hours || 0;
    }
    return { actual, estimated };
  }, [tasks]);

  // Court-intelligence: collect distinct phase names from tasks
  const phaseNames = useMemo(() => {
    const phases = new Map<string, number>();
    for (const t of tasks) {
      const phaseName = t.court_metadata?.phase_name;
      const phaseOrder = t.court_metadata?.phase_order ?? 999;
      if (phaseName && !phases.has(phaseName)) {
        phases.set(phaseName, phaseOrder);
      }
    }
    return Array.from(phases.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([name, order]) => ({ name, order }));
  }, [tasks]);

  const hasCourtMetadata = phaseNames.length > 0;

  // Apply phase filter on top of status/priority filters
  const phaseFilteredTasks = useMemo(() => {
    if (selectedPhase === 'all') return filteredTasks;
    return filteredTasks.filter(t => t.court_metadata?.phase_name === selectedPhase);
  }, [filteredTasks, selectedPhase]);

  // Group tasks by phase (sorted by phase_order)
  const groupedByPhase = useMemo(() => {
    if (!groupByPhase) return null;
    const groups: { phaseName: string; phaseOrder: number; tasks: Task[] }[] = [];
    const phaseMap = new Map<string, Task[]>();
    const ungrouped: Task[] = [];

    for (const task of phaseFilteredTasks) {
      const phaseName = task.court_metadata?.phase_name;
      if (phaseName) {
        if (!phaseMap.has(phaseName)) phaseMap.set(phaseName, []);
        phaseMap.get(phaseName)!.push(task);
      } else {
        ungrouped.push(task);
      }
    }

    for (const { name, order } of phaseNames) {
      const phaseTasks = phaseMap.get(name);
      if (phaseTasks && phaseTasks.length > 0) {
        groups.push({ phaseName: name, phaseOrder: order, tasks: phaseTasks });
      }
    }
    if (ungrouped.length > 0) {
      groups.push({ phaseName: 'Uncategorized', phaseOrder: 9999, tasks: ungrouped });
    }

    return groups;
  }, [groupByPhase, phaseFilteredTasks, phaseNames]);

  // ── Render a single compact task row ──
  const renderTaskCard = (task: Task) => {
    const overdue = isOverdue(task.due_date, task.status);
    const hasDescription = !!task.description;
    const hasHours = task.estimated_hours != null || task.actual_hours != null;
    const hasBillingCode = !!task.billing_code;
    const hasTags = task.tags && task.tags.length > 0;
    const hasSubtasks = (task.subtask_count || 0) > 0;
    const hasSecondRow = hasDescription || hasHours || hasBillingCode || hasTags || hasSubtasks;
    const hasDeadlineTrace = !!task.court_metadata?.deadline_calculation_trace;
    const initials = getInitials(task);
    const assigneeName = getAssigneeName(task);

    return (
      <div
        key={task.id}
        className={`border border-l-[3px] rounded-lg transition-colors ${getPriorityBorder(task.priority)} ${
          overdue
            ? 'border-red-300 dark:border-red-700 border-l-red-500 bg-red-50/30 dark:bg-red-900/10'
            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
        }`}
      >
        {/* Row 1: Title + badges + meta + actions */}
        <div className="flex items-center gap-2 sm:gap-3 px-3 py-2.5">
          {/* Title + badges */}
          <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
            <h5
              onClick={() => setSelectedTaskId(task.id)}
              className="text-sm font-medium text-gray-900 dark:text-white truncate cursor-pointer hover:text-lex-accent transition-colors"
              title={task.title}
            >
              {task.title}
            </h5>
            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full shrink-0 ${getStatusColor(task.status)}`}>
              {getStatusLabel(task.status)}
            </span>
            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full shrink-0 ${getPriorityColor(task.priority)}`}>
              {task.priority}
            </span>
            {task.court_metadata?.is_court_mandated && (
              <CourtMandatedBadge
                mandateReference={task.court_metadata.mandate_reference}
                statuteReference={task.court_metadata.deadline_calculation_trace?.statute_citation}
              />
            )}
            {task.court_metadata?.required_forms && task.court_metadata.required_forms.length > 0 && (
              <FormRequiredChip forms={task.court_metadata.required_forms} />
            )}
          </div>

          {/* Assignee + Due date */}
          <div className="hidden sm:flex items-center gap-2.5 shrink-0">
            {initials && (
              <div
                className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-300 shrink-0"
                title={assigneeName}
              >
                {initials}
              </div>
            )}
            <span
              className={`text-xs whitespace-nowrap ${
                overdue
                  ? 'text-red-600 dark:text-red-400 font-semibold'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {formatDate(task.due_date)}
              {overdue && ' !'}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Timer — only show active timer on the task that owns it */}
            <TimeTracker
              compact
              activeTimer={activeTimerTaskId === task.id ? activeTimer : null}
              onStart={(dto) => {
                setActiveTimerTaskId(task.id);
                return createAndStartTimer({
                  ...dto,
                  activity_category: dto.activity_category || (!isUtbmsCode(task.billing_code) ? task.billing_code : undefined) || undefined,
                  utbms_code: dto.utbms_code || (isUtbmsCode(task.billing_code) ? task.billing_code : undefined) || undefined,
                });
              }}
              onPause={pauseTimer}
              onResume={startTimer}
              onStop={stopTimer}
              onComplete={async (entry) => {
                setActiveTimerTaskId(null);
                const loggedHours = Number(entry.duration) || 0;
                if (loggedHours > 0) {
                  const currentHours = task.actual_hours || 0;
                  await taskApi.update(task.id, { actual_hours: currentHours + loggedHours });
                  await fetchTasks();
                }
              }}
              defaultDescription={task.title}
              defaultActivityCategory={!isUtbmsCode(task.billing_code) ? task.billing_code || undefined : undefined}
              defaultUtbmsCode={isUtbmsCode(task.billing_code) ? task.billing_code! : undefined}
            />

            {/* Log Time */}
            <button
              onClick={() => handleLogTime(task)}
              className="p-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors"
              title="Log time"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
            </button>

            {/* Status dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger>
                <button
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Change status"
                >
                  <div className={`w-3.5 h-3.5 rounded-full ${getStatusDot(task.status)} ring-1 ring-black/10 dark:ring-white/20`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {STATUS_OPTIONS.map(s => (
                  <DropdownMenuItem
                    key={s.value}
                    onClick={() => handleStatusChange(task.id, s.value)}
                    className={task.status === s.value ? 'font-semibold' : ''}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                      {s.label}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Kebab menu */}
            <DropdownMenu>
              <DropdownMenuTrigger>
                <button
                  className="p-1.5 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                  title="More actions"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSelectedTaskId(task.id)}>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.64 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.64 0-8.573-3.007-9.963-7.178Z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                    Open Details
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedTaskId(task.id)}>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                    </svg>
                    Edit
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => handleDeleteClick(task.id)}
                  className="text-red-600 dark:text-red-400"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                    Delete
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Row 2: Description + hours + billing code + tags + subtasks (conditional) */}
        {hasSecondRow && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 pb-2 text-xs text-gray-500 dark:text-gray-400">
            {hasDescription && (
              <p className="truncate max-w-md">{task.description}</p>
            )}
            {hasHours && (
              <div className="flex items-center gap-1.5 shrink-0">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <span className="tabular-nums">
                  {task.actual_hours ?? 0}{task.estimated_hours != null ? ` / ${task.estimated_hours}` : ''} hrs
                </span>
                {task.estimated_hours != null && task.estimated_hours > 0 && (
                  <div className="w-14 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${getHoursProgressColor(task.actual_hours || 0, task.estimated_hours)}`}
                      style={{ width: `${Math.min(((task.actual_hours || 0) / task.estimated_hours) * 100, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}
            {hasBillingCode && (
              <span className="px-1.5 py-0.5 bg-lex-accent-soft text-lex-accent rounded font-mono text-[10px]">
                {task.billing_code}
              </span>
            )}
            {hasSubtasks && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                {task.subtaskProgress
                  ? `${task.subtaskProgress.completed}/${task.subtaskProgress.total}`
                  : `${task.subtask_count} subtask${task.subtask_count! > 1 ? 's' : ''}`
                }
              </span>
            )}
            {hasTags && task.tags!.map(tag => (
              <span key={tag} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]">{tag}</span>
            ))}
            {/* Mobile: show assignee + due date here */}
            <div className="flex sm:hidden items-center gap-2 text-xs">
              {initials && (
                <span className="text-gray-500 dark:text-gray-400">{assigneeName}</span>
              )}
              <span className={overdue ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
                {formatDate(task.due_date)}{overdue ? ' !' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Row 3: Deadline trace (conditional, collapsible) */}
        {hasDeadlineTrace && (
          <div className="px-3 pb-2">
            <DeadlineTracePanel
              trace={{
                ...task.court_metadata!.deadline_calculation_trace!,
                trigger_event: (task.court_metadata as any)?.statutory_deadline?.triggered_by,
                trigger_date: task.legal_context?.trigger_date || task.legal_context?.trigger_event,
                calculation_type: (task.court_metadata as any)?.statutory_deadline?.calculation,
                base_days: (task.court_metadata as any)?.statutory_deadline?.due_date_offset_days
                  ?? (task.court_metadata as any)?.template_due_date_offset_days,
              }}
              statuteReference={task.legal_context?.statute_reference || task.legal_context?.statute_of_limitations}
            />
          </div>
        )}

        {/* Expandable: Inline Log Time Form */}
        {loggingTimeForTaskId === task.id && (
          <div className="mx-3 mb-2.5 mt-0.5 px-3 py-2.5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-md">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Date</label>
                  <input
                    type="date"
                    value={timeForm.entry_date}
                    onChange={(e) => setTimeForm(prev => ({ ...prev, entry_date: e.target.value }))}
                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Hours</label>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={timeForm.duration}
                    onChange={(e) => setTimeForm(prev => ({ ...prev, duration: e.target.value }))}
                    placeholder="1.5"
                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Rate ($/hr)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={timeForm.rate}
                    onChange={(e) => setTimeForm(prev => ({ ...prev, rate: e.target.value }))}
                    placeholder="250"
                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Description</label>
                  <input
                    type="text"
                    value={timeForm.description}
                    onChange={(e) => setTimeForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {timeForm.duration && timeForm.rate && (
                  <span className="text-[10px] tabular-nums text-gray-500 dark:text-gray-400">
                    {formatCurrency(parseFloat(timeForm.duration) * parseFloat(timeForm.rate))}
                  </span>
                )}
                <button
                  onClick={() => setLoggingTimeForTaskId(null)}
                  className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTimeSubmit}
                  disabled={timeSubmitting || !timeForm.duration}
                  className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors disabled:opacity-50"
                >
                  {timeSubmitting ? 'Saving...' : 'Log'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-500 dark:text-gray-400">Loading tasks...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Tasks</h3>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Task Summary KPI Cards */}
      <div className={`grid grid-cols-2 sm:grid-cols-3 ${hoursBudget.estimated > 0 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-4`}>
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Total Tasks</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{taskSummary.total}</div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="text-yellow-600 dark:text-yellow-400 text-sm font-medium">To Do</div>
          <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-100 tabular-nums">{taskSummary.todo}</div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="text-blue-600 dark:text-blue-400 text-sm font-medium">In Progress</div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100 tabular-nums">{taskSummary.inProgress}</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <div className="text-green-600 dark:text-green-400 text-sm font-medium">Done</div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100 tabular-nums">{taskSummary.done}</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
          <div className="text-red-600 dark:text-red-400 text-sm font-medium">Overdue</div>
          <div className="text-2xl font-bold text-red-900 dark:text-red-100 tabular-nums">{taskSummary.overdue}</div>
        </div>
        {hoursBudget.estimated > 0 && (
          <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <div className="text-indigo-600 dark:text-indigo-400 text-sm font-medium">Hours Budget</div>
            <div className="text-2xl font-bold text-indigo-900 dark:text-indigo-100 tabular-nums">
              {hoursBudget.actual}<span className="text-base font-normal text-indigo-400 dark:text-indigo-500">/{hoursBudget.estimated}</span>
            </div>
            <div className="w-full h-1.5 bg-indigo-200 dark:bg-indigo-800 rounded-full overflow-hidden mt-1.5">
              <div
                className={`h-full rounded-full ${getHoursProgressColor(hoursBudget.actual, hoursBudget.estimated)}`}
                style={{ width: `${Math.min((hoursBudget.actual / hoursBudget.estimated) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full sm:w-auto"
          >
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full sm:w-auto"
          >
            <option value="all">All Priorities</option>
            {PRIORITY_OPTIONS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>

          {/* Phase filter — only shown when tasks have court metadata */}
          {hasCourtMetadata && (
            <>
              <select
                value={selectedPhase}
                onChange={(e) => setSelectedPhase(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full sm:w-auto"
              >
                <option value="all">All Phases</option>
                {phaseNames.map(p => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => setGroupByPhase(!groupByPhase)}
                className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors w-full sm:w-auto ${
                  groupByPhase
                    ? 'border-lex-accent bg-lex-accent-soft text-lex-accent'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Group by Phase
              </button>
            </>
          )}
        </div>

        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors w-full sm:w-auto"
        >
          Add Task
        </button>
      </div>

      {/* Add Task Form */}
      {showAddForm && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add New Task</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Task Title</label>
              <input
                type="text"
                value={newTask.title || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter task title..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Date</label>
              <input
                type="date"
                value={newTask.due_date || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
              <select
                value={newTask.priority || 'medium'}
                onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value as TaskPriority }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {PRIORITY_OPTIONS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={newTask.status || 'todo'}
                onChange={(e) => setNewTask(prev => ({ ...prev, status: e.target.value as TaskStatus }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="review">In Review</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assign To</label>
              <select
                value={newTask.assignee_id || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, assignee_id: e.target.value || undefined }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Me (default)</option>
                {realmUsers.map(u => (
                  <option key={u.id} value={u.keycloakUserId || u.id}>{u.name} ({u.userType})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estimated Hours</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={newTask.estimated_hours ?? ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, estimated_hours: e.target.value ? parseFloat(e.target.value) : undefined }))}
                placeholder="e.g. 4.0"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <UtbmsCodeSelector
              value={newTask.billing_code || ''}
              onChange={(code) => setNewTask(prev => ({ ...prev, billing_code: code }))}
              label="Billing Code"
              placeholder="e.g. L110"
            />
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                rows={3}
                value={newTask.description || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Task description..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateTask}
              disabled={submitting || !newTask.title?.trim()}
              className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors w-full sm:w-auto disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Task'}
            </button>
          </div>
        </div>
      )}

      {/* Tasks List */}
      <div className="space-y-2">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">Task List</h4>

        {/* Grouped by Phase view */}
        {groupByPhase && groupedByPhase && groupedByPhase.length > 0 && (
          <div className="space-y-6">
            {groupedByPhase.map((group) => {
              const doneTasks = group.tasks.filter(t => t.status === 'done').length;
              const progressPct = group.tasks.length > 0 ? Math.round((doneTasks / group.tasks.length) * 100) : 0;
              return (
                <div key={group.phaseName} className="space-y-2">
                  {/* Phase header */}
                  <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-lex-accent-soft text-lex-accent text-xs font-bold">
                        {group.phaseOrder}
                      </span>
                      <h5 className="text-sm font-semibold text-gray-900 dark:text-white">{group.phaseName}</h5>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 tabular-nums">{progressPct}%</span>
                    </div>
                  </div>
                  {/* Phase tasks */}
                  <div className="space-y-2 pl-2 border-l-2 border-lex-accent ml-3">
                    {group.tasks.map(renderTaskCard)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Flat view (default) */}
        {(!groupByPhase || !groupedByPhase || groupedByPhase.length === 0) && (
        <div className="space-y-2">
          {phaseFilteredTasks.map(renderTaskCard)}

          {phaseFilteredTasks.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {tasks.length === 0 ? 'No tasks yet. Click "Add Task" to create one.' : 'No tasks found matching your criteria.'}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={!!selectedTaskId}
        taskId={selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        onUpdated={fetchTasks}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmTaskId && (() => {
        const taskToDelete = tasks.find(t => t.id === deleteConfirmTaskId);
        const hasSubtasks = taskToDelete?.subtask_count && taskToDelete.subtask_count > 0;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/60" onClick={() => setDeleteConfirmTaskId(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Task</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{taskToDelete?.title}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                Are you sure you want to delete this task? This action cannot be undone.
              </p>
              {hasSubtasks && (
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
                  <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                    This task has {taskToDelete.subtask_count} subtask{taskToDelete.subtask_count! > 1 ? 's' : ''}.
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Deleting this task will also remove all subtasks and notify assigned users.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 mt-5">
                <button
                  onClick={() => setDeleteConfirmTaskId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Delete Task
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default TasksTab;
